const contactTagName = 'jesoli-contact';

class JesoliContact extends HTMLElement {
  #abortController;
  #initialized = false;
  #observer;

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.destroy();
  }

  initialize() {
    if (this.#initialized) return;

    this.#initialized = true;
    this.#abortController = new AbortController();
    this.addEventListener('submit', this.#handleSubmit, {
      capture: true,
      signal: this.#abortController.signal,
    });
    this.#enhancePhoneField();

    this.#observer = new MutationObserver(() => this.#enhancePhoneField());
    this.#observer.observe(this, { childList: true, subtree: true });
  }

  destroy() {
    if (!this.#initialized) return;

    this.#observer?.disconnect();
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#observer = undefined;
    this.#initialized = false;
  }

  #enhancePhoneField() {
    const phone = this.querySelector('[data-jesoli-phone-visible]');
    if (!(phone instanceof HTMLInputElement)) {
      this.#applyStandaloneOptionalPhonePresentation();
      return;
    }

    phone.type = 'tel';
    phone.autocomplete = 'tel';
    phone.inputMode = 'tel';
    phone.removeAttribute('required');
    phone.removeAttribute('aria-required');
    phone.removeAttribute('pattern');

    const form = phone.closest('form');
    if (!(form instanceof HTMLFormElement)) return;

    let normalized = form.querySelector('[data-jesoli-phone-normalized]');
    if (!(normalized instanceof HTMLInputElement)) {
      const phoneField = phone.closest('[data-jesoli-phone-field]');
      if (!(phoneField instanceof HTMLElement)) return;

      normalized = document.createElement('input');
      normalized.type = 'hidden';
      normalized.name = 'contact[phone]';
      normalized.dataset.jesoliPhoneNormalized = '';
      phoneField.insertAdjacentElement('afterend', normalized);
    }

    phone.removeAttribute('name');
    normalized.value = this.#normalizePhone(phone.value);
  }

  #applyStandaloneOptionalPhonePresentation() {
    const phone = this.querySelector('input[name="contact[phone]"]');
    if (!(phone instanceof HTMLInputElement)) return;

    phone.removeAttribute('required');
    phone.removeAttribute('aria-required');
    if (phone.placeholder !== 'Phone (optional)') {
      phone.placeholder = 'Phone (optional)';
    }

    const label = this.querySelector(`label[for="${phone.id}"]`);
    if (label?.textContent?.trim() !== 'Phone (optional)') {
      label.textContent = 'Phone (optional)';
    }
  }

  #handleSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.contains(form)) return;

    const phone = form.querySelector('[data-jesoli-phone-visible]');
    const normalized = form.querySelector('[data-jesoli-phone-normalized]');
    if (!(phone instanceof HTMLInputElement) || !(normalized instanceof HTMLInputElement)) return;

    normalized.value = this.#normalizePhone(phone.value);
  };

  #normalizePhone(rawValue) {
    const compact = rawValue.trim().replace(/[\s()\-]/g, '');
    if (!compact) return '';

    const startsWithPlus = compact.startsWith('+');
    const digits = compact.replace(/\D/g, '');
    if (!digits) return '';

    if (digits.startsWith('61')) {
      let nationalNumber = digits.slice(2);
      while (nationalNumber.startsWith('61')) nationalNumber = nationalNumber.slice(2);
      if (nationalNumber.startsWith('0')) nationalNumber = nationalNumber.slice(1);
      return `+61${nationalNumber}`;
    }

    if (digits.startsWith('04')) return `+61${digits.slice(1)}`;
    if (digits.startsWith('4')) return `+61${digits}`;
    if (digits.startsWith('0')) return `+61${digits.slice(1)}`;
    return startsWithPlus ? `+${digits}` : digits;
  }
}

if (!customElements.get(contactTagName)) {
  customElements.define(contactTagName, JesoliContact);
}
